import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { SignatureRequestService } from 'src/app/services/signature-request.service';
import { SignaturesHeaderService } from 'src/app/services/signatures-header.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { JsonResponseSingle } from 'src/app/models/json-response.interface';
import { SignatoryBasicInfo } from 'src/app/models/signatory-basic-info.model';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-signatory-request-otp-code',
  templateUrl: './signatory-request-otp-code.component.html',
  styleUrls: ['./signatory-request-otp-code.component.sass']
})
export class SignatoryRequestOtpCodeComponent implements OnInit, AfterViewInit {

  private signatory_uuid:string = "";
  public pageStatus:'loading'|'valid'|'invalidated'|'error' = 'loading';
  public email:string = null as any;
  public loading:boolean = false;
  public name:string = null as any;
  public isRecaptchaNeeded:boolean = true || environment.production;
  public recaptchaToken:string = null as any;
  public error:boolean = false;
  public error_message:string = "";
  private lastSignatureRequestDescription:string = "";

  constructor(private header:SignaturesHeaderService,
              private router:Router,
              private activated_route:ActivatedRoute,
              private api:ApiService,
              private auth:AuthService,
              private signature_request:SignatureRequestService,
              private recaptchaV3Service: ReCaptchaV3Service,
              private translate: TranslateService) {
    this.signatory_uuid = this.activated_route.snapshot.queryParamMap.get('signatory_uuid') as string;
  }

  ngOnInit(): void {
    this.auth.resetLoginProcess();
    this.header.setAuthStep(0);
    this.initRecaptcha();
    this.initSignatory();
    this.listenLoginProcess();
  }

  ngAfterViewInit(): void {
    // Solo abrimos la cabecera de "documento a firmar" en el flujo válido.
    if(this.pageStatus === 'valid') this.openHeader();
  }

  submit() {
    this.loading = true;
    const signature_request_uuid = this.auth.getLoginProcess().signature_request_uuid;
    this.api.requestOtp(this.email, signature_request_uuid, this.recaptchaToken).subscribe(
      data => {
        this.loading = false;
        this.router.navigate(['/signatures', 'auth', 'verify']);
      },
      error => {
        this.loading = false;
        this.error = true;
        this.error_message = error.error.message || this.translate.instant('unexpectedError');

        if (error.error.message === 'browser-error') {
          Sentry.captureException(error);
        }
      }
    );
  }

  private async openHeader() {
    const response = await fetch('/assets/img/svg/password-illustration.svg');
    let svg = await response.text();

    this.translate.onLangChange.subscribe(
      data => {
        // transformar colores
        let documentToSignTranslation = this.translate.instant("documentToSign");
        this.header.openHeader(documentToSignTranslation, this.lastSignatureRequestDescription, svg);
      }
    );

    this.signature_request.signature_request_description.subscribe(
      data => {
        this.lastSignatureRequestDescription = data;
        let documentToSignTranslation = this.translate.instant("documentToSign");
        this.header.openHeader(documentToSignTranslation, data, svg);
      }
    );
  }

  private initSignatory() {
    const response:JsonResponseSingle<SignatoryBasicInfo>|null = this.activated_route.snapshot.data['basic_info'];

    // El resolver devuelve null ante 404 / error de red: estado de error, NO la pantalla de invalidado.
    if(response == null) {
      this.pageStatus = 'error';
      return;
    }

    const data = response.data;

    // El link puede estar invalidado a nivel de solicitud o de firmante concreto.
    if(data.invalidated) {
      this.signature_request.setCompany(data.company); // branding para la pantalla bloqueada
      this.pageStatus = 'invalidated';
      return;
    }

    this.auth.setLoginProcess({
      signature_request_uuid: data.signature_request_uuid,
      signatory_uuid: this.signatory_uuid,
      name: data.name,
      company: data.company,
      email: data.email,
      phone: data.phone as string
    });

    this.signature_request.setCompany(data.company);
    this.pageStatus = 'valid';

    this.router.navigate([]);
  }

  private listenLoginProcess() {
    this.auth.loginProcess.subscribe(
      data => {
        if(data != null) {
          this.email = data.email;
          this.name = data.name;
        }
      }
    );
  }

  private initRecaptcha() {
    if(this.isRecaptchaNeeded) {
      this.recaptchaV3Service.execute('request_otp').subscribe(
        token => {
          this.recaptchaToken = token;
        }
      );
    }
  }
}
